import { evaluate as celEvaluate } from 'cel-js'

/**
 * TokenContext is an opaque interface for resolving token values in schema expressions.
 * This is imported from @cibo/dynamic-uniforms (the forms library).
 *
 * We define it here optionally for circular dependency prevention.
 * If not provided (undefined), template expressions won't be evaluated.
 */
export interface TokenContext {
  get(name: string): unknown
}

/**
 * Evaluate a CEL expression string using the provided token context.
 * Extracts variable values from tokenContext by scanning identifier names in the expression.
 */
function evaluateCelExpression(
  expr: string,
  tokenContext: TokenContext
): unknown {
  try {
    // Extract identifier names from expression to build context object
    const identifierRegex = /\b([a-zA-Z_]\w*)\b/g
    const context: Record<string, unknown> = {}
    let match
    while ((match = identifierRegex.exec(expr)) !== null) {
      const name = match[1]
      const value = tokenContext.get(name)
      if (value !== undefined) {
        context[name] = value
      }
    }

    return celEvaluate(expr, context, { string: (x: unknown) => String(x) })
  } catch {
    return undefined
  }
}

/**
 * Evaluates template expressions in the ${...} format.
 * Handles both standalone expressions (return raw value) and embedded expressions (return string).
 *
 * @example
 * evaluateTemplateString('${year - 1}', ctx) // 2023 (number)
 * evaluateTemplateString('${year - 1}-04-16', ctx) // '2023-04-16' (string)
 * evaluateTemplateString('number:${year - 1}', ctx) // 2023 (explicit number)
 * evaluateTemplateString('string:${year - 1}', ctx) // '2023' (explicit string)
 * evaluateTemplateString('array:[${year}, ${month}]', ctx) // [2024, 5] (array)
 */
function evaluateTemplateString(
  str: string,
  tokenContext: TokenContext
): unknown {
  // Check for explicit type prefix
  const numberMatch = str.match(/^number:(.+)$/)
  const stringMatch = str.match(/^string:(.+)$/)
  const arrayMatch = str.match(/^array:(.+)$/)

  let expressionStr = str
  let forceType: 'number' | 'string' | 'array' | null = null

  if (numberMatch) {
    expressionStr = numberMatch[1]
    forceType = 'number'
  } else if (stringMatch) {
    expressionStr = stringMatch[1]
    forceType = 'string'
  } else if (arrayMatch) {
    expressionStr = arrayMatch[1]
    forceType = 'array'
  }

  // Pattern to find all ${...} expressions
  const templatePattern = /\$\{([^}]+)\}/g

  // Check if entire string is just one ${...} (solo expression)
  const soloMatch = expressionStr.match(/^\$\{([^}]+)\}$/)

  if (soloMatch) {
    // Solo expression: evaluate and determine type
    const exprResult = evaluateCelExpression(soloMatch[1], tokenContext)

    if (forceType === 'string') {
      return String(exprResult)
    }
    if (forceType === 'number' || forceType === null) {
      return exprResult
    }
    if (forceType === 'array') {
      return Array.isArray(exprResult) ? exprResult : [exprResult]
    }
    return exprResult
  }

  if (forceType === 'array') {
    // Array type: parse and evaluate each element
    const arrayContent = expressionStr.slice(1, -1) // Remove [ ]
    const elements = arrayContent.split(',').map((el) => {
      const trimmed = el.trim()
      // Check if it's a quoted template expression: "${expr}" or '${expr}' → return as string
      const quotedElementMatch = trimmed.match(/^"(\$\{[^}]+\})"$/) || trimmed.match(/^'(\$\{[^}]+\})'$/)
      if (quotedElementMatch) {
        return String(evaluateCelExpression(quotedElementMatch[1].slice(2, -1), tokenContext))
      }
      // Check if it's an unquoted template expression: ${expr} → return raw value
      const elementMatch = trimmed.match(/^\$\{([^}]+)\}$/)
      if (elementMatch) {
        return evaluateCelExpression(elementMatch[1], tokenContext)
      }
      // Remove quotes if present (static string)
      if ((trimmed.startsWith('"') && trimmed.endsWith('"')) ||
          (trimmed.startsWith("'") && trimmed.endsWith("'"))) {
        return trimmed.slice(1, -1)
      }
      return trimmed
    })
    return elements
  }

  // Embedded expressions or string type: replace all ${...} with evaluated values
  let result = expressionStr
  let hasMatch = false
  let hasUndefined = false

  result = result.replace(templatePattern, (match, expr) => {
    hasMatch = true
    const evaluated = evaluateCelExpression(expr, tokenContext)
    if (evaluated === undefined) {
      hasUndefined = true
    }
    return String(evaluated)
  })

  // If any variable was missing/undefined, return undefined
  if (hasUndefined) {
    return undefined
  }

  // Return as string if embedded expressions, or if explicitly string type
  if (forceType === 'string' || hasMatch) {
    return result
  }

  return result
}

/**
 * Evaluate CEL expressions in schema property values.
 * Looks for objects with a `cel` key and evaluates the expression.
 * Also handles ${...} template expressions in strings.
 * Recursively evaluates all values in arrays and objects.
 *
 * Supports:
 * - Template strings: ${...} with CEL expressions
 * - CEL objects: { cel: "..." } for schema constraints  
 * - Type prefixes: number:, string:, array: for explicit type conversion
 * - Full CEL expressions: +, -, *, /, %, ==, !=, <, >, <=, >=, &&, ||, !, ternary
 *
 * @param value The value to evaluate (may contain templates or CEL objects)
 * @param tokenContext Object providing variable values via get(name) method
 * @returns Evaluated value with all expressions resolved
 *
 * @example
 * // Template strings
 * evaluateTemplateExpression('${year - 1}-04-16', ctx) => '2023-04-16'
 * evaluateTemplateExpression('${year - 1}', ctx) => 2023
 *
 * // CEL objects
 * evaluateTemplateExpression({ cel: "year - 1" }, ctx) => 2023
 * evaluateTemplateExpression({ minimum: { cel: "year - 1" } }, ctx) => { minimum: 2023 }
 *
 * // Arrays and objects
 * evaluateTemplateExpression(['${year}', { cel: "year - 1" }], ctx) => [2024, 2023]
 */
export function evaluateTemplateExpression(
  value: unknown,
  tokenContext?: TokenContext
): unknown {
  // Without tokenContext, only pass through non-template values
  if (!tokenContext) {
    return value
  }

  // Check if this is a CEL object: { cel: "expression" }
  if (
    value !== null &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    'cel' in value
  ) {
    const celObj = value as Record<string, unknown>
    if (typeof celObj.cel === 'string') {
      return evaluateCelExpression(celObj.cel, tokenContext)
    }
  }

  // Handle template strings: ${...}
  if (typeof value === 'string') {
    return evaluateTemplateString(value, tokenContext)
  }

  // Handle arrays - recursively evaluate each element
  if (Array.isArray(value)) {
    return value.map((item) => evaluateTemplateExpression(item, tokenContext))
  }

  // Handle plain objects - recursively evaluate all properties
  if (value !== null && typeof value === 'object') {
    const result: Record<string, unknown> = {}
    const obj = value as Record<string, unknown>
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        result[key] = evaluateTemplateExpression(obj[key], tokenContext)
      }
    }
    return result
  }

  // Primitives pass through unchanged
  return value
}
